import React, { useState, useEffect } from 'react';
import './PacketGenerator.css';
import packetData from '../data/Aleph2025-Questions.json';
import PacketView from './PacketView';
import Sidebar from '../components/Sidebar';

const EnhancedPacketGenerator = () => {
  // Core state
  const [darkMode, setDarkMode] = useState(true);
  const [packets, setPackets] = useState([]);
  const [generatedPackets, setGeneratedPackets] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [isViewingPacket, setIsViewingPacket] = useState(false);
  
  // Add this to your existing state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Add these functions after your existing functions
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (!sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Add this useEffect after your existing useEffect
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    difficulty: 'Medium',
    numQuestions: '',
    author: '',
    dateOfPlayedQuestions: ''
  });

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    author: false,
    date: false,
    difficulty: false,
    answerQuestion: false,
    source: false
  });

  // Available options (derived from data)
  const [availableOptions, setAvailableOptions] = useState({
    difficulties: ['Beginner', 'Medium', 'Difficult'],
    authors: [],
    dates: []
  });

  // Initialize data on mount
  useEffect(() => {
    if (packetData?.packets && Array.isArray(packetData.packets)) {
      setPackets(packetData.packets);
      
      // Extract available options from data
      const difficulties = new Set();
      const authors = new Set();
      const dates = new Set();
      
      packetData.packets.forEach(packet => {
        if (packet.difficulty) difficulties.add(packet.difficulty);
        if (packet.date) dates.add(packet.date);
        if (packet.author) {
          if (Array.isArray(packet.author)) {
            packet.author.forEach(auth => authors.add(auth));
          } else {
            authors.add(packet.author);
          }
        }
      });
      
      setAvailableOptions({
        difficulties: Array.from(difficulties),
        authors: Array.from(authors),
        dates: Array.from(dates)
      });

      // Set initial form values
      if (difficulties.size > 0) setFormData(prev => ({ ...prev, difficulty: Array.from(difficulties)[0] }));
      
      // Set some example generated packets for display
      setGeneratedPackets([
        ...packetData.packets.map((packet, index) => ({
          ...packet,
          id: `display-${index}`,
          season: index % 2 === 0 ? 'Autumn' : 'Spring'
        }))
      ]);
    }
  }, []);

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Generate packets based on criteria
  const handleGeneratePackets = () => {
    setIsGenerating(true);
    
    setTimeout(() => {
      // Filter packets based on form criteria
      let filteredPackets = packets.filter(packet => {
        const matchesDifficulty = !formData.difficulty || packet.difficulty === formData.difficulty;
        const matchesAuthor = !formData.author || (
          Array.isArray(packet.author) 
            ? packet.author.includes(formData.author)
            : packet.author === formData.author
        );
        const matchesDate = !formData.dateOfPlayedQuestions || packet.date === formData.dateOfPlayedQuestions;
        
        return matchesDifficulty && matchesAuthor && matchesDate;
      });

      // Process question count if specified
      if (formData.numQuestions && formData.numQuestions.trim() !== '') {
        const requestedCount = parseInt(formData.numQuestions);
        
        filteredPackets = filteredPackets.map(packet => {
          const newPacket = { ...packet };
          newPacket.questions = requestedCount;
          
          // Adjust questions_data if it exists
          if (newPacket.questions_data) {
            if (newPacket.questions_data.length > requestedCount) {
              newPacket.questions_data = newPacket.questions_data.slice(0, requestedCount);
            } else if (newPacket.questions_data.length < requestedCount) {
              // Add placeholder questions
              const needed = requestedCount - newPacket.questions_data.length;
              for (let i = 0; i < needed; i++) {
                newPacket.questions_data.push({
                  id: `gen-${Date.now()}-${i}`,
                  text: `Generated Question ${newPacket.questions_data.length + 1}`,
                  answer: "Generated Answer",
                  author: newPacket.author?.[0] || "System"
                });
              }
            }
          }
          
          return newPacket;
        });
      }

      // Create variations for display
      const variations = [];
      filteredPackets.forEach((packet, packetIndex) => {
        for (let i = 1; i <= 3; i++) {
          variations.push({
            ...packet,
            id: `${packet.id}-var-${i}`,
            title: `${packet.title} - Set ${i}`,
            season: i % 2 === 0 ? 'Spring' : 'Autumn',
            source: 'Generated'
          });
        }
      });
      
      setGeneratedPackets(variations.length > 0 ? variations : filteredPackets);
      setIsGenerating(false);
    }, 1000);
  };

  // Handle packet view
  const handleViewPacket = (packet) => {
    setSelectedPacket(packet);
    setIsViewingPacket(true);
  };

  const handleReturnToGenerator = () => {
    setIsViewingPacket(false);
    setSelectedPacket(null);
  };

  // Toggle filter
  const toggleFilter = (filterName) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Filter packets for display
  const getFilteredPackets = () => {
    if (!searchQuery.trim() && !Object.values(activeFilters).some(v => v)) {
      return generatedPackets;
    }

    return generatedPackets.filter(packet => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      let matchesSearch = false;

      // Default search in title
      if (packet.title?.toLowerCase().includes(query)) {
        matchesSearch = true;
      }

      // Search in specific fields if filters are active
      if (activeFilters.author && packet.author) {
        const authorText = Array.isArray(packet.author) ? packet.author.join(' ') : packet.author;
        if (authorText.toLowerCase().includes(query)) {
          matchesSearch = true;
        }
      }
      
      if (activeFilters.date && packet.date?.includes(query)) {
        matchesSearch = true;
      }
      
      if (activeFilters.difficulty && packet.difficulty?.toLowerCase().includes(query)) {
        matchesSearch = true;
      }
      
      if (activeFilters.answerQuestion && packet.questions_data) {
        const hasMatch = packet.questions_data.some(q => 
          q.text?.toLowerCase().includes(query) || q.answer?.toLowerCase().includes(query)
        );
        if (hasMatch) matchesSearch = true;
      }

      return matchesSearch;
    });
  };

  // Show PacketView if viewing a packet
  if (isViewingPacket && selectedPacket) {
    return <PacketView packet={selectedPacket} onReturn={handleReturnToGenerator} />;
  }

  return (
    <div className="pg-app-container">
      {/* Add burger menu */}
      <button className="burger-menu" onClick={toggleSidebar}>
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
          <path d="M3 12h18M3 6h18M3 18h18"></path>
        </svg>
      </button>
      
      {/* Add overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay visible" onClick={handleOverlayClick}></div>
      )}
      
      {/* Update sidebar structure */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="sidebar-close" onClick={toggleSidebar} aria-label="Close sidebar">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </button>
        <div className="sidebar-content-wrapper">
          <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} />
        </div>
      </div>
      
      {/* FIXED: Removed duplicate pg-main-content div */}
      <div className="pg-main-content">
        <h1 className="pg-page-title">პაკეტის გენერირება</h1>
        
        {/* Generation Form */}
        <div className="pg-form-container">

          <div className="pg-form-row">
            <div className="pg-form-label">კითხვების რაოდენობა:</div>
            <div className="pg-form-field">
              <input 
                type="number" 
                placeholder="რაოდენობა"
                value={formData.numQuestions}
                onChange={(e) => handleFormChange('numQuestions', e.target.value)}
                min="1"
                max="50"
                className="pg-styled-input"
              />
            </div>
          </div>
          
          <div className="pg-form-row">
            <div className="pg-form-label">ავტორი:</div>
            <div className="pg-form-field">
              <div className="pg-select-wrapper">
                <select 
                  value={formData.author}
                  onChange={(e) => handleFormChange('author', e.target.value)}
                  className="pg-styled-select"
                >
                  <option className="pg-dropdown" value="">ავტორი</option>
                  {availableOptions.authors.map(auth => (
                    <option key={auth} value={auth}>{auth}</option>
                  ))}
                </select>
                <div className="pg-select-arrow">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pg-form-row">
            <div className="pg-form-label">თარიღი:</div>
            <div className="pg-form-field">
              <div className="pg-select-wrapper">
                <select 
                  value={formData.dateOfPlayedQuestions}
                  onChange={(e) => handleFormChange('dateOfPlayedQuestions', e.target.value)}
                  className="pg-styled-select"
                >
                  <option value="">თარიღი</option>
                  {availableOptions.dates.map(date => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
                <div className="pg-select-arrow">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pg-form-actions">
            <button 
              className="pg-generate-button"
              onClick={handleGeneratePackets}
              disabled={isGenerating}
            >
              {isGenerating ? 'პაკეტი გენერირდება...' : 'პაკეტის გენერირება'}
            </button>
          </div>
        </div>
        
        {/* Results Section */}
        <div className="pg-packets-section">
          <h2 className="pg-section-title">დაგენერირებული პაკეტები ({getFilteredPackets().length})</h2>
          
          {/* Results Table */}
          <div className="pg-packets-table">
            {generatedPackets.length > 0 ? (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>სათაური</th>
                      <th>კითხვების რაოდენობა</th>
                      <th>ავტორი</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredPackets().slice(0, 10).map((packet) => (
                      <tr key={packet.id} onClick={() => handleViewPacket(packet)} style={{ cursor: 'pointer' }}>
                        <td title={packet.title}>{packet.title}</td>
                        <td>{packet.questions || 0}</td>
                        <td>{Array.isArray(packet.author) ? packet.author.join(', ') : packet.author}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {getFilteredPackets().length > 10 && (
                  <div className="pg-see-all-link">
                    <span>10 / {getFilteredPackets().length} შედეგი</span>
                  </div>
                )}
              </>
            ) : (
              <div className="pg-no-results">
                <p>პაკეტები ჯერ არ არის გენერირებული. დააკონფიგურირეთ თქვენი პარამეტრები და დააჭირეთ "პაკეტების გენერირება" რომ დაიწყოთ.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedPacketGenerator;